import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from './authStore';
import type { 
  BusinessUnit, 
  BusinessUnitType,
  BusinessUnitStatus,
  BusinessUnitContext,
  OrganizationHierarchy,
  Company,
  Branch,
  PointOfSale,
  BusinessUnitOption
} from '../types/business-unit';
import type { BusinessUnitFilterBase } from '../types/filters';
import type { AuthBusinessUnit } from '../types/auth';

/**
 * Interface pour le filtre effectif dérivé de la sélection d'unité
 * Utilisé par tous les hooks pour passer aux API endpoints
 */
interface EffectiveFilter extends BusinessUnitFilterBase {
  businessUnitId?: string;
  businessUnitType?: BusinessUnitType;
  businessUnitIds?: string[];
}

/**
 * Scope de l'utilisateur - détermine les unités accessibles
 */
type UserScope = 'company' | 'unit';

/**
 * État du store pour la gestion des unités commerciales
 */
interface BusinessUnitState {
  // Données de la hiérarchie organisationnelle
  hierarchy: OrganizationHierarchy | null;
  
  // Unité commerciale actuellement sélectionnée pour le filtrage
  selectedUnit: BusinessUnit | null;
  
  // Alias pour compatibilité avec les hooks existants
  selectedBusinessUnit: BusinessUnit | null;
  
  // Contexte de filtrage actuel
  filterContext: BusinessUnitContext | null;
  
  // Filtre effectif pour les API (computed)
  effectiveFilter: EffectiveFilter;
  
  // Options aplaties pour le sélecteur
  unitOptions: BusinessUnitOption[];
  
  // Indicateur de chargement
  loading: boolean;
  
  // Inclure les données des sous-unités dans les filtres
  includeChildren: boolean;
  
  // Scope de l'utilisateur depuis authStore
  userScope: UserScope | null;
  
  // Business unit assignée à l'utilisateur (depuis authStore)
  userBusinessUnit: AuthBusinessUnit | null;
  
  // Actions
  setHierarchy: (hierarchy: OrganizationHierarchy) => void;
  selectUnit: (unit: BusinessUnit | null) => void;
  selectUnitById: (unitId: string) => void;
  setIncludeChildren: (include: boolean) => void;
  resetToCompany: () => void;
  setLoading: (loading: boolean) => void;
  initializeFromAuth: () => void;
  
  // Getters
  getSelectedUnitIds: () => string[];
  isCompanyLevel: () => boolean;
  isBranchLevel: () => boolean;
  isPosLevel: () => boolean;
  canSelectUnit: (unitId: string) => boolean;
}

/**
 * Store Zustand pour la gestion des unités commerciales
 * Permet de filtrer les données comptables par niveau organisationnel :
 * - Entreprise (niveau global, par défaut)
 * - Succursale
 * - Point de vente
 * 
 * Intégration avec authStore:
 * - userScope: 'company' = accès à toute la hiérarchie
 * - userScope: 'unit' = accès limité à l'unité assignée et ses enfants
 */
export const useBusinessUnitStore = create<BusinessUnitState>()(
  persist(
    (set, get) => ({
      hierarchy: null,
      selectedUnit: null,
      filterContext: null,
      unitOptions: [],
      loading: false,
      includeChildren: true,
      userScope: null,
      userBusinessUnit: null,
      
      // Getter computed pour selectedBusinessUnit (alias)
      get selectedBusinessUnit() {
        return get().selectedUnit;
      },
      
      // Getter computed pour effectiveFilter
      get effectiveFilter(): EffectiveFilter {
        const { selectedUnit, hierarchy, includeChildren } = get();
        
        if (!selectedUnit || !hierarchy) {
          return {};
        }
        
        const businessUnitIds = includeChildren 
          ? getChildUnitIdsInternal(selectedUnit.id, selectedUnit.type, hierarchy)
          : [selectedUnit.id];
        
        return {
          businessUnitId: selectedUnit.id,
          businessUnitType: selectedUnit.type,
          businessUnitIds
        };
      },
      
      /**
       * Initialise le store depuis authStore
       * À appeler après authStore.initialize()
       */
      initializeFromAuth: () => {
        const authState = useAuthStore.getState();
        const { businessUnit, organization } = authState;
        const userScope = authState.getUserScope();
        
        set({ 
          userScope, 
          userBusinessUnit: businessUnit 
        });
        
        console.log('🔗 BusinessUnitStore initialisé depuis authStore:', {
          userScope,
          businessUnitId: businessUnit?.id,
          businessUnitType: businessUnit?.type,
          organizationId: organization?.id
        });
      },
      
      /**
       * Vérifie si l'utilisateur peut sélectionner une unité donnée
       * Basé sur son scope et son unité assignée
       */
      canSelectUnit: (unitId: string) => {
        const { userScope, userBusinessUnit, hierarchy } = get();
        
        // Scope company = accès total
        if (userScope === 'company') {
          return true;
        }
        
        // Scope unit = accès limité à l'unité assignée et ses enfants
        if (userScope === 'unit' && userBusinessUnit && hierarchy) {
          // Vérifier si unitId est l'unité assignée ou un enfant
          const allowedIds = getChildUnitIdsInternal(
            userBusinessUnit.id, 
            userBusinessUnit.type as BusinessUnitType, 
            hierarchy
          );
          return allowedIds.includes(unitId);
        }
        
        return false;
      },
      
      /**
       * Définit la hiérarchie organisationnelle complète
       */
      setHierarchy: (hierarchy) => {
        const { userScope, userBusinessUnit } = get();
        const options = flattenHierarchyToOptionsInternal(hierarchy);
        
        // Filtrer les options selon le scope utilisateur
        const filteredOptions = userScope === 'unit' && userBusinessUnit
          ? options.filter(opt => {
              const allowedIds = getChildUnitIdsInternal(
                userBusinessUnit.id, 
                userBusinessUnit.type as BusinessUnitType, 
                hierarchy
              );
              return allowedIds.includes(opt.id);
            })
          : options;
        
        // Déterminer l'unité par défaut selon le scope
        let defaultUnit: BusinessUnit;
        if (userScope === 'unit' && userBusinessUnit) {
          // Scope unit: sélectionner l'unité assignée à l'utilisateur
          const assignedUnit = findUnitById(userBusinessUnit.id, hierarchy);
          defaultUnit = assignedUnit || hierarchy.company;
        } else {
          // Scope company: sélectionner l'entreprise par défaut
          defaultUnit = hierarchy.company;
        }
        
        // Créer le contexte de filtrage pour l'unité par défaut
        let defaultContext: BusinessUnitContext;
        if (defaultUnit.type === 'company') {
          defaultContext = { level: 'company', companyId: defaultUnit.id };
        } else if (defaultUnit.type === 'branch') {
          defaultContext = { 
            level: 'branch', 
            companyId: hierarchy.company.id,
            branchId: defaultUnit.id 
          };
        } else {
          const pos = defaultUnit as PointOfSale;
          defaultContext = { 
            level: 'pos', 
            companyId: hierarchy.company.id,
            branchId: pos.branchId,
            posId: pos.id 
          };
        }
        
        set({ 
          hierarchy,
          unitOptions: filteredOptions,
          selectedUnit: defaultUnit,
          filterContext: defaultContext
        });
      },
      
      /**
       * Sélectionne une unité commerciale pour le filtrage
       */
      /**
       * Sélectionne une unité commerciale pour le filtrage
       * Vérifie que l'utilisateur a le droit d'accéder à cette unité
       */
      selectUnit: (unit) => {
        const { hierarchy, canSelectUnit } = get();
        
        if (!unit || !hierarchy) {
          set({ selectedUnit: null, filterContext: null });
          return;
        }
        
        // Vérifier le droit d'accès à cette unité
        if (!canSelectUnit(unit.id)) {
          console.warn('⚠️ Accès refusé à l\'unité:', unit.id, unit.name);
          return;
        }
        
        let context: BusinessUnitContext;
        
        if (unit.type === 'company') {
          context = {
            level: 'company',
            companyId: unit.id
          };
        } else if (unit.type === 'branch') {
          context = {
            level: 'branch',
            companyId: hierarchy.company.id,
            branchId: unit.id
          };
        } else {
          // Point de vente
          const pos = unit as PointOfSale;
          context = {
            level: 'pos',
            companyId: hierarchy.company.id,
            branchId: pos.branchId,
            posId: unit.id
          };
        }
        
        set({ selectedUnit: unit, filterContext: context });
      },
      
      /**
       * Sélectionne une unité par son ID
       * Vérifie les droits d'accès via canSelectUnit
       */
      selectUnitById: (unitId) => {
        const { hierarchy, canSelectUnit } = get();
        if (!hierarchy) return;
        
        // Vérifier le droit d'accès
        if (!canSelectUnit(unitId)) {
          console.warn('⚠️ Accès refusé à l\'unité ID:', unitId);
          return;
        }
        
        // Chercher dans la hiérarchie
        if (hierarchy.company.id === unitId) {
          get().selectUnit(hierarchy.company);
          return;
        }
        
        const branch = hierarchy.branches.find(b => b.id === unitId);
        if (branch) {
          get().selectUnit(branch);
          return;
        }
        
        const pos = hierarchy.pointsOfSale.find(p => p.id === unitId);
        if (pos) {
          get().selectUnit(pos);
          return;
        }
      },
      
      /**
       * Définit si les données des sous-unités doivent être incluses
       */
      setIncludeChildren: (include) => set({ includeChildren: include }),
      
      /**
       * Réinitialise la sélection au niveau entreprise
       */
      resetToCompany: () => {
        const { hierarchy } = get();
        if (hierarchy) {
          get().selectUnit(hierarchy.company);
        }
      },
      
      /**
       * Définit l'état de chargement
       */
      setLoading: (loading) => set({ loading }),
      
      /**
       * Retourne les IDs des unités à inclure dans les filtres
       */
      getSelectedUnitIds: () => {
        const { selectedUnit, hierarchy, includeChildren } = get();
        
        if (!selectedUnit || !hierarchy) {
          return [];
        }
        
        if (!includeChildren) {
          return [selectedUnit.id];
        }
        
        return getChildUnitIdsInternal(selectedUnit.id, selectedUnit.type, hierarchy);
      },
      
      /**
       * Vérifie si le niveau actuel est l'entreprise
       */
      isCompanyLevel: () => {
        const { selectedUnit } = get();
        return selectedUnit?.type === 'company';
      },
      
      /**
       * Vérifie si le niveau actuel est une succursale
       */
      isBranchLevel: () => {
        const { selectedUnit } = get();
        return selectedUnit?.type === 'branch';
      },
      
      /**
       * Vérifie si le niveau actuel est un point de vente
       */
      isPosLevel: () => {
        const { selectedUnit } = get();
        return selectedUnit?.type === 'pos';
      }
    }),
    {
      name: 'business-unit-storage',
      partialize: (state) => ({
        // Ne persister que l'ID de l'unité sélectionnée et la préférence includeChildren
        selectedUnitId: state.selectedUnit?.id,
        includeChildren: state.includeChildren
      })
    }
  )
);

/**
 * Fonction utilitaire interne pour aplatir la hiérarchie
 */
function flattenHierarchyToOptionsInternal(
  hierarchy: OrganizationHierarchy
): BusinessUnitOption[] {
  const options: BusinessUnitOption[] = [];
  
  // Vérifier que la hiérarchie est valide
  if (!hierarchy || !hierarchy.company) {
    console.warn('flattenHierarchyToOptionsInternal: hiérarchie invalide');
    return options;
  }
  
  // Ajouter l'entreprise (niveau 0)
  options.push({
    id: hierarchy.company.id,
    code: hierarchy.company.code,
    name: hierarchy.company.name,
    type: 'company',
    level: 0,
    fullPath: hierarchy.company.name
  });
  
  // Ajouter les succursales (niveau 1) - avec protection
  const branches = Array.isArray(hierarchy.branches) ? hierarchy.branches : [];
  const pointsOfSale = Array.isArray(hierarchy.pointsOfSale) ? hierarchy.pointsOfSale : [];
  
  branches.forEach(branch => {
    options.push({
      id: branch.id,
      code: branch.code,
      name: branch.name,
      type: 'branch',
      level: 1,
      parentName: hierarchy.company.name,
      fullPath: `${hierarchy.company.name} > ${branch.name}`
    });
    
    // Ajouter les points de vente de cette succursale (niveau 2)
    const branchPOS = pointsOfSale.filter(pos => pos.branchId === branch.id);
    branchPOS.forEach(pos => {
      options.push({
        id: pos.id,
        code: pos.code,
        name: pos.name,
        type: 'pos',
        level: 2,
        parentName: branch.name,
        fullPath: `${hierarchy.company.name} > ${branch.name} > ${pos.name}`
      });
    });
  });
  
  return options;
}

/**
 * Fonction utilitaire interne pour obtenir les IDs des unités enfants
 */
function getChildUnitIdsInternal(
  unitId: string,
  unitType: BusinessUnitType,
  hierarchy: OrganizationHierarchy
): string[] {
  const ids: string[] = [unitId];
  
  // Protection contre une hiérarchie invalide
  if (!hierarchy || !hierarchy.company) {
    return ids;
  }
  
  const branches = Array.isArray(hierarchy.branches) ? hierarchy.branches : [];
  const pointsOfSale = Array.isArray(hierarchy.pointsOfSale) ? hierarchy.pointsOfSale : [];
  
  if (unitType === 'company') {
    // Ajouter toutes les succursales et points de vente
    branches.forEach(branch => {
      ids.push(branch.id);
    });
    pointsOfSale.forEach(pos => {
      ids.push(pos.id);
    });
  } else if (unitType === 'branch') {
    // Ajouter tous les points de vente de cette succursale
    pointsOfSale
      .filter(pos => pos.branchId === unitId)
      .forEach(pos => {
        ids.push(pos.id);
      });
  }
  // Si c'est un point de vente, pas d'enfants
  
  return ids;
}

/**
 * Fonction utilitaire interne pour trouver une unité par son ID
 */
function findUnitById(
  unitId: string,
  hierarchy: OrganizationHierarchy
): BusinessUnit | null {
  // Protection contre une hiérarchie invalide
  if (!hierarchy || !hierarchy.company) {
    return null;
  }
  
  // Vérifier si c'est l'entreprise
  if (hierarchy.company.id === unitId) {
    return hierarchy.company;
  }
  
  const branches = Array.isArray(hierarchy.branches) ? hierarchy.branches : [];
  const pointsOfSale = Array.isArray(hierarchy.pointsOfSale) ? hierarchy.pointsOfSale : [];
  
  // Chercher dans les succursales
  const branch = branches.find(b => b.id === unitId);
  if (branch) {
    return branch;
  }
  
  // Chercher dans les points de vente
  const pos = pointsOfSale.find(p => p.id === unitId);
  if (pos) {
    return pos;
  }
  
  return null;
}
